import React, {useEffect, useState} from "react";
import {VsiBubble, VsiH4, VsiLgText} from "../../../styled/App.styled";
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import Grid from "../../../components/Grid";
import SunburstChart from "../../../components/SunburstChart";
import Highcharts from "highcharts/highstock";
import {ButtonGroup, Card, Container, Spinner} from "reactstrap";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {databaseVersionDefs} from "../../../utils/gridColumnDefs/databases/databaseVersionDefs";
import {
    booleanFormatter,
    healthColorAlertFormatter, integerFormatter,
    removeURIEncode, ServiceNowButton, serviceNowButton,
    shortDateFormatter
} from "../../../utils/gridFormatters";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import ColumnChart from "../../../components/ColumnChart";
import {beforeAfterOld} from "../../../utils/chartUtils";
import VerticalBarChart from "../../../components/VerticalBarChart";
import AcknowledgeButton from "../../../components/AcknowledgeButton";
import {useNavigate, useSearchParams} from "react-router-dom";
import {getReport, PostAuth, postReport} from "../../../utils/Verbs";
import dayjs from "dayjs";

const colors = {
    "MSSQLSERVER_DATABASE": "#fEb800",
    "ORACLE_DATABASE": "#c74634",
    "DB2_DATABASE": "#0f62fe"
};
const chartTypes = [
    {value: "all", label: "All"},
    {value: 1, label: "Yes"},
    {value: 0, label: "No"}
];
const chartOptionsList = [
    {value: "all", label: "All"},
    {value: "yellow", label: "Warning"},
    {value: "red", label: "Alert"}
];
export default function DatabaseHealthAlerts({title}) {

    useTitleFromComponent(title);

    const {theme, client, user, getAccessTokenSilently} = useVsiAppContext();
    const [searchParams, setSearchParams] = useSearchParams();
    let [data, {isLoading}] = useDataWithRoute(`/api/v6/report/database_health_proc?client_id=${client.clientId}`);

    const [selectedAlerts, setSelectedAlerts] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [chartCategories, setChartCategories] = useState([]);
    const [gridData, setGridData] = useState([]);
    const [isAcknowledging, setIsAcknowledging] = useState(false);
    const [color, setColor] = useState("#fEb800");

    const type = searchParams.get("type");
    let defaultType = chartTypes.find(({value}) => type === value) || chartTypes[2];
    const [chartType, setChartType] = useState(defaultType);

    const option = searchParams.get("severity");
    let defaultOption = chartOptionsList.find(({value}) => option === value) || chartOptionsList[0];
    const [chartOption, setChartOption] = useState(defaultOption);
    let navigate = useNavigate();
    const [currData, setCurrData] = useState({});
    const [allowAck, setAllowAck] = useState(false);
    const [alertCount, setAlertCount] = useState({
        acknowledged: 0,
        unacknowledged: 0
    });


    useEffect(() => {
        setCurrData(data);
    }, [data]);

    useEffect(() => {
        loadData(currData?.recordsets?.[1], currData?.recordsets?.[0], chartType, chartOption);
    }, [currData, chartType, chartOption]);

    const loadData = (grid_data, chart_data, type, option) => {

        let filteredData = (grid_data || []);
        if (type.value !== "all") {
            filteredData = filteredData.filter(f => f.acknowledged === type.value);
        }
        if (option.value !== "all") {
            filteredData = filteredData.filter(f => f.severity === option.value);
        }
        setGridData(filteredData);

        setColor(colors["MSSQLSERVER_DATABASE"]);


        let chartFilteredData = (chart_data || []);
        if (type.value !== "all") {
            chartFilteredData = chartFilteredData.filter(f => f.acknowledged === type.value);
        }
        if (option.value !== "all") {
            chartFilteredData = chartFilteredData.filter(f => f.severity === option.value);
        }


        setChartCategories(
            chartFilteredData.map(m => m.alert)
        );

        setChartData([
            {
                name: "Alerts",
                data: chartFilteredData.map(m => m.database_alert_count)
            }
        ]);
    };

    const onTypeChange = (type) => {
        setChartType(type);
        searchParams.set("type", type.value);
        setSearchParams(searchParams, {replace: true});
    };

    const onOptionChange = (option) => {
        setChartOption(option);
        searchParams.set("severity", option.value);
        setSearchParams(searchParams, {replace: true});
    };

    const onAcknowledge = async () => {
        if (selectedAlerts.length < 1 || isAcknowledging) return;
        let selectedRows = [...selectedAlerts];

        const route = `/api/v6/report/database_health_acknowledge_alert_proc`;
        const token = await getAccessTokenSilently();

        for (const idx in selectedRows) {
            const selRow = selectedRows[idx];
            const payload = {
                "database_health_id": selRow.database_health_id,
                "user_name": user.email,
                "ack": 1
            };
            await postReport(route, payload, token);
            if (idx === selectedRows.length - 1) {
                const newData = await getReport(`/api/v6/report/database_health_proc?client_id=${client.clientId}`, null, token);
                setCurrData(newData.data);
            }
        }
    };


    const onRowSelected = (e) => {
        const alertCount = {
            acknowledged: 0,
            unacknowledged: 0
        };
        const alerts = [...(e?.api?.getSelectedNodes?.() ?? [])].map(({data}) => {
            if (!data.acknowledged) {
                alertCount.acknowledged = alertCount.acknowledged + 1;
            } else if (data.acknowledged) {
                alertCount.unacknowledged = alertCount.unacknowledged + 1;
            }
            return data;
        });
        setSelectedAlerts(alerts);
        setAlertCount(alertCount);
    };
    const onRowClick = (e) => {
        // This will be used on the future... I am keeping it...
        // navigate(`/reports/storage/device/device-health?device_name=${encodeURI(e.data.device_name)}&wwn=${e.data.wwn}&period_id=${periodId}`)
    };

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Databases Health Alerts</VsiH4>
            {isLoading ? <Spinner color={"primary"}/> : (
                <div>
                    <VsiBubble noAutobot>
                        <div className={"w-100 p-2 d-flex"}>
                            <VsiLabeledSelect options={chartTypes}
                                              isDisabled={isLoading || isAcknowledging}
                                              isLoading={isLoading || isAcknowledging}
                                              label={"Acknowledged"}
                                              minWidth={150}
                                              onChange={onTypeChange}
                                              defaultValue={chartType}/>
                            <VsiLabeledSelect options={chartOptionsList}
                                              isLoading={isLoading || isAcknowledging}
                                              isDisabled={isLoading || isAcknowledging}
                                              label={"Severity"}
                                              minWidth={200}
                                              maxWidth={500}
                                              onChange={onOptionChange}
                                              defaultValue={chartOption}/>


                        </div>
                    </VsiBubble>
                    <VsiBubble autobotName="ColumnChart">
                        <Container>
                            <VerticalBarChart
                                categories={chartCategories}
                                series={chartData}
                                // colors={[color]}
                                changeAxisTitle={true}
                                yTitle1={"Count of Alerts"}
                                height={450}
                                axisMaxMin={{axis1Min: 0}}
                            />
                        </Container>
                    </VsiBubble>
                    <VsiBubble>
                        <VsiLgText>
                            {`You can ${chartType.value === "acknowledged" ? "un" : ""}acknowledge (${chartType.value === "acknowledged" ? "show" : "hide"}) alerts. Use Shift + Click on the grid below to select multiple alerts at once`}
                        </VsiLgText>
                        <div className="d-flex align-items-end justify-content-between">
                            <ButtonGroup>
                                <AcknowledgeButton allowAcknowledge={allowAck}
                                                   acknowledgedToggle={chartType.value === "acknowledged"}
                                                   isLoading={isLoading || isAcknowledging}
                                                   buttonDisplayOverride={chartType.value === "all" ? `Acknowledge ${alertCount.acknowledged}, Unacknowledge ${alertCount.unacknowledged}` : null}
                                                   onAcknowledge={onAcknowledge}
                                                   disable={(isLoading || isAcknowledging) || chartType.value === "all" ? !(alertCount.acknowledged > 0 || alertCount.unacknowledged > 0) : selectedAlerts.length < 1}
                                />
                                <VsiLgText>
                                    {allowAck ? "" : `  Please select up to 10 rows to proceed acknowledge.`}
                                </VsiLgText>
                            </ButtonGroup>
                        </div>
                        <Grid addStyle
                              data={gridData}
                              isLoading={isLoading}
                              rowDoubleClicked={onRowClick}
                              onRowSelected={onRowSelected}
                              onSelectionChanged={(params) => {
                                  let selectedRows = params.current.api.getSelectedRows();
                                  if (selectedRows.length > 10) {
                                      setAllowAck(false);
                                  } else {
                                      setAllowAck(true);
                                  }
                              }}
                              disable={(isLoading || isAcknowledging) || chartType.value === "all"}
                              columnDefs={[
                                  {
                                      field: "id",
                                      headerName: 'ID'
                                  },
                                  {
                                      "field": "severity",
                                      "headerName": "Severity",
                                      "cellRenderer": healthColorAlertFormatter
                                  },
                                  {
                                      "field": "database_server",
                                      "headerName": "Database Server"
                                  },
                                  {
                                      "field": "database_instance",
                                      "headerName": "Database Instance"
                                  },
                                  {
                                      "field": "category",
                                      "headerName": "Category"
                                  },
                                  {
                                      "field": "alert",
                                      "headerName": "Alert",
                                      "cellRenderer": removeURIEncode
                                  },
                                  {
                                      "field": "description",
                                      "headerName": "Description",
                                      "cellRenderer": removeURIEncode
                                  },
                                  {
                                      "field": "acknowledged",
                                      "headerName": "Acknowledged?",
                                      "cellRenderer": booleanFormatter
                                  },
                                  {
                                      "field": "incident_id",
                                      "headerName": "Service Now",
                                      "cellRenderer": ServiceNowButton
                                  }
                              ]}
                        />
                    </VsiBubble>
                </div>
            )}
        </Card>
    );
}
;
